type IConvertUrlToFileArgs = {
  url: string;
  filename?: string;
};

const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "webp",
  "svg",
  "ico",
  "tiff",
  "tif",
];

export const FileUtils = {
  extractFilename(url: string): string {
    const { pathname } = new URL(url); // decoded path e.g. /.../file.jpg
    return pathname.split("/").pop() || "";
  },

  getFileExtension(name: string): string {
    const dot = name.lastIndexOf(".");
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
  },

  isImageFile(file: File | string): boolean {
    try {
      const name = typeof file === "string" ? file : file.name;
      const ext = this.getFileExtension(name);

      if (typeof file !== "string" && file.type.startsWith("image/")) {
        return true;
      }

      return IMAGE_EXTENSIONS.includes(ext);
    } catch {
      return false;
    }
  },

  createImagePreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  },

  filterNewFiles(files: File[]): File[] {
    return files.filter((file) => !(file as any).__originalUrl);
  },

  generateUniqueFilename(file: File, index = 0): string {
    const ext = this.getFileExtension(file.name);
    return `${Date.now()}_${index}${ext ? `.${ext}` : ""}`;
  },

  renameFile(file: File, newName: string): File {
    return new File([file], newName, { type: file.type });
  },

  async convertUrlToFile({
    url,
    filename,
  }: IConvertUrlToFileArgs): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();

    const urlObj = new URL(url);

    const decodedPath = decodeURIComponent(urlObj.pathname);

    const originalName = decodedPath.split("/").pop() || "";
    const ext = this.getFileExtension(originalName);

    const finalName = filename
      ? ext
        ? `${filename}.${ext}`
        : filename
      : originalName || "file";

    return new File([blob], finalName, { type: blob.type });
  },

  convertFileToFormData(name: string, file: File): FormData {
    const fd = new FormData();
    fd.append(name, file);
    return fd;
  },

  convertFilesToFormData(name: string, files: File[]): FormData[] {
    return files.map((file) => this.convertFileToFormData(name, file));
  },

  async download(url: string, desiredName?: string) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      const original = this.extractFilename(url);
      const ext = this.getFileExtension(original);

      const finalName =
        desiredName && ext ? `${desiredName}.${ext}` : original || "file";

      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = finalName;
      a.click();

      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: browser tries to infer filename
      const a = document.createElement("a");
      a.href = url;
      a.download = desiredName || "file";
      a.click();
    }
  },
};
